/**
 * Central seam for the report editor's core state. Everything that needs a
 * stable identity across the other hooks lives here:
 *
 *  - `currentReport` / `setCurrentReport` (with full setStateAction support
 *    so callers can use `setCurrentReport(prev => ...)` for safe edits)
 *  - `serverBaseline` (anchor for `unsavedChanges`)
 *  - `status` / `conclusion` (seeded from the server payload)
 *  - `findings` map (seeded from worksheet + report payload)
 *  - `conclusionEditorRef` — imperative handle into the Conclusions editor
 *    (Copilot markdown insertion).
 *
 * Effects: seed-from-reportData, init-findings-from-worksheet,
 * hydrate-findings-from-report-payload, and the unmount cleanup that
 * dispatches `clearMeasurement()` on study switch.
 *
 * Derived: `measurementsRecord`, `annotatedMeasurements`, `contextRecord`.
 * These read `currentReport` and are used by the worksheet slots.
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useDispatch } from 'react-redux';
import { clearMeasurement } from '@/state/slices/measurementSession';
import type { RichTextEditorHandle } from '@/features/report';
import {
  Context,
  FindingStatement,
  Measurement,
  MeasurementTool,
  Report as MReport,
} from '@/models/report';
import { Worksheet } from '@/models/template';
import { reconcileReport } from '../utils/reconcileReport';
import {
  aiDisplayValues,
  conclusionSignature,
  fingerprintFromReport,
  fingerprintMeasurement,
  type ReportEditBaseline,
} from '../utils/reportEditBaseline';
import { useSeedReportFindings } from './useSeedReportFindings';
import { useWorksheetTextHistory } from './useWorksheetTextHistory';
import { contextFromMeasurements } from '../derived/contextMeasurements';

/** Stable empty array so the recalc hook's value signature doesn't churn when
 *  there is no report loaded. */
const NO_MEASUREMENTS: Measurement[] = [];

type ReportStatus = 'DRAFT' | 'PRELIMINARY' | 'FINAL';

interface UseReportEditorStateArgs {
  reportData: MReport | undefined;
  reportWorksheet: Worksheet | undefined;
}

export interface ReportEditorState {
  currentReport: MReport | null;
  setCurrentReport: Dispatch<SetStateAction<MReport | null>>;
  serverBaseline: ReportEditBaseline | null;
  setServerBaseline: Dispatch<SetStateAction<ReportEditBaseline | null>>;
  status: ReportStatus;
  setStatus: Dispatch<SetStateAction<ReportStatus>>;
  conclusion: { uuid?: string; value: string } | null;
  setConclusion: Dispatch<
    SetStateAction<{ uuid?: string; value: string } | null>
  >;
  handleConclusionChange: (value: string) => void;
  /** AP-773 (A7): the conclusion editor commits on blur — focus marks the
   *  baseline, blur records one step if the text changed since. */
  handleConclusionFocus: () => void;
  handleConclusionBlur: () => void;
  findings: Record<string, FindingStatement[]>;
  setFindings: Dispatch<SetStateAction<Record<string, FindingStatement[]>>>;
  handleFindingChange: (
    findingClass: string,
    value: FindingStatement[]
  ) => void;
  conclusionEditorRef: React.RefObject<RichTextEditorHandle | null>;
  measurementsRecord: Record<
    string,
    { value: number; unit: string; valueSource?: 'SR' | 'MANUAL' | null }
  >;
  measurementToolByCanonical: Record<string, MeasurementTool | null>;
  annotatedMeasurements: Set<string>;
  /** Derived/indexed rows keyed by `canonicalMeasurement` (uuid) → their raw
   *  `formula` ('' when the label has none). Sourced from the worksheet TEMPLATE
   *  labels (AP-632: formula/type live there, not on the report measurement).
   *  Flags a row as auto-computed: shows the passive ƒ indicator, suppresses the
   *  SR/AI dot, and (later) gates it read-only. */
  /** Derived/indexed rows (by `canonicalMeasurement` uuid) the engine
   *  auto-updated since the last save — their fingerprint differs from the
   *  server baseline. Drives the orange unsaved-change outline (R7); clears
   *  when the baseline resets on save/refetch. */
  unsavedMeasurements: Set<string>;
  contextRecord: Record<string, Context>;
}

export function useReportEditorState({
  reportData,
  reportWorksheet,
}: UseReportEditorStateArgs): ReportEditorState {
  const dispatch = useDispatch();

  // Clear stale measurement state on unmount (study switch).
  useEffect(
    () => () => {
      dispatch(clearMeasurement());
    },
    [dispatch]
  );

  const [status, setStatus] = useState<ReportStatus>('DRAFT');
  const [currentReport, setCurrentReport] = useState<MReport | null>(null);
  const [serverBaseline, setServerBaseline] =
    useState<ReportEditBaseline | null>(null);
  const [conclusion, setConclusion] = useState<{
    uuid?: string;
    value: string;
  } | null>(null);
  const [findings, setFindings] = useState<Record<string, FindingStatement[]>>(
    {}
  );

  // Ref mirror of the baseline so the reseed effect can read the latest value
  // without depending on it (depending on it would re-run the reseed on every
  // save and defeat the reconcile below).
  const serverBaselineRef = useRef(serverBaseline);
  useEffect(() => {
    serverBaselineRef.current = serverBaseline;
  }, [serverBaseline]);

  // UUID of the report we last seeded from. Lets the reseed distinguish a
  // post-save refetch of the *same* report (reconcile) from an initial load or
  // study switch (adopt wholesale).
  const lastReportUuidRef = useRef<string | undefined>(undefined);

  // Seed local editor state from the server payload. Runs on initial load
  // and after every post-mutation refetch — including the explicit baseline
  // reset so derived `unsavedChanges` flips to false.
  //
  // On a post-save refetch of the same report, in-flight local edits are
  // preserved instead of adopted wholesale — see `reconcileReport`.
  useEffect(() => {
    if (!reportData) {
      setCurrentReport(null);
      setConclusion(null);
      setServerBaseline(null);
      lastReportUuidRef.current = undefined;
      return;
    }

    const baseline = serverBaselineRef.current;
    const sameReport =
      lastReportUuidRef.current === reportData.uuid && !!baseline;

    setCurrentReport((prev) =>
      sameReport && prev && baseline
        ? reconcileReport(reportData, prev, baseline)
        : reportData
    );

    setStatus(reportData.status as ReportStatus);

    const serverConclusion = reportData.conclusion
      ? {
          uuid: reportData.conclusion.uuid,
          value: reportData.conclusion.value,
        }
      : null;
    if (!sameReport) {
      setConclusion(serverConclusion);
    } else {
      setConclusion((prev) => {
        // Must use the same normalization the baseline was captured with —
        // a raw trim of the editor's HTML never matches it, so the server
        // conclusion would never be adopted.
        const localNorm = conclusionSignature(prev?.value ?? '');
        const dirty = localNorm !== baseline.conclusionNormalized;
        return dirty ? prev : serverConclusion;
      });
    }

    setServerBaseline(fingerprintFromReport(reportData));
    lastReportUuidRef.current = reportData.uuid;
  }, [reportData]);

  // Seed default empty entries for any finding class declared by the
  // worksheet but not yet present in the report.
  useEffect(() => {
    if (!reportWorksheet) return;
    setFindings((prev) => {
      const next: Record<string, FindingStatement[]> = { ...prev };
      reportWorksheet.findingGroups.forEach((f) => {
        const findingClass = f.findingLabels?.[0]?.findingClass;
        if (findingClass && !(findingClass in next)) {
          next[findingClass] = [];
        }
      });
      return next;
    });
  }, [reportWorksheet]);

  // Seed findings from the loaded report (adopt on first load, preserve
  // in-flight edits on a same-report refetch). See the hook for the rationale.
  useSeedReportFindings(currentReport, serverBaselineRef, setFindings);

  // AP-773: findings and the conclusion commit history steps at their
  // §3.1 commit points (A6 / A7).
  const {
    handleFindingChange,
    handleConclusionChange,
    handleConclusionFocus,
    handleConclusionBlur,
  } = useWorksheetTextHistory({
    findings,
    setFindings,
    conclusion,
    setConclusion,
  });

  // Shared ref into the Conclusions editor (Copilot markdown insertion).
  // Lives here so hooks that need it share one identity.
  const conclusionEditorRef = useRef<RichTextEditorHandle>(null);

  const measurementsRecord = useMemo<
    Record<
      string,
      { value: number; unit: string; valueSource?: 'SR' | 'MANUAL' | null }
    >
  >(() => {
    if (!currentReport?.measurements) return {};
    return currentReport.measurements.reduce(
      (acc, m) => {
        if (m.canonicalMeasurement && m.value != null) {
          acc[m.canonicalMeasurement] = {
            value: m.value,
            unit: m.unit,
            valueSource: m.valueSource,
          };
        }
        return acc;
      },
      {} as Record<
        string,
        { value: number; unit: string; valueSource?: 'SR' | 'MANUAL' | null }
      >
    );
  }, [currentReport?.measurements]);

  // Dense tool lookup over ALL report rows (not filtered by value), so even
  // an as-yet-unmeasured drawable row can be made clickable. Keyed by
  // canonicalMeasurement — the same join the worksheet uses for values.
  const measurementToolByCanonical = useMemo<
    Record<string, MeasurementTool | null>
  >(() => {
    if (!currentReport?.measurements) return {};
    return currentReport.measurements.reduce(
      (acc, m) => {
        if (m.canonicalMeasurement) {
          acc[m.canonicalMeasurement] = m.tool ?? null;
        }
        return acc;
      },
      {} as Record<string, MeasurementTool | null>
    );
  }, [currentReport?.measurements]);

  const annotatedMeasurements = useMemo<Set<string>>(() => {
    if (!currentReport?.measurements) return new Set();
    return new Set(
      currentReport.measurements
        .filter(
          (m): m is typeof m & { canonicalMeasurement: string } =>
            !!m.canonicalMeasurement && !!m.annotation
        )
        .map((m) => m.canonicalMeasurement)
    );
  }, [currentReport?.measurements]);

  // R7 (AP-764): a row is unsaved when its current fingerprint differs from the
  // server baseline — a plain dirty flag covering ANY edit (manual, adopted AI,
  // or engine auto-update), not just computed rows. The baseline only resets on
  // save/refetch, so the flag persists until save and clears for every row at once.
  const unsavedMeasurements = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    if (!currentReport?.measurements || !serverBaseline) return set;
    const aiValues = aiDisplayValues(currentReport.aiMeasurements);
    currentReport.measurements.forEach((m) => {
      const key = m.canonicalMeasurement;
      if (!key) return;
      if (
        serverBaseline.measurementByCanonical.get(key) !==
        fingerprintMeasurement(m, aiValues.get(key))
      ) {
        set.add(key);
      }
    });
    return set;
  }, [
    currentReport?.measurements,
    currentReport?.aiMeasurements,
    serverBaseline,
  ]);

  const contextRecord = useMemo<Record<string, Context>>(() => {
    const resolved = (currentReport?.context ?? []).reduce(
      (acc, c) => {
        if (c.contextClass) acc[c.contextClass] = c;
        return acc;
      },
      {} as Record<string, Context>
    );
    return {
      ...resolved,
      ...contextFromMeasurements(
        currentReport?.measurements ?? NO_MEASUREMENTS
      ),
    };
  }, [currentReport?.context, currentReport?.measurements]);

  return {
    currentReport,
    setCurrentReport,
    serverBaseline,
    setServerBaseline,
    status,
    setStatus,
    conclusion,
    setConclusion,
    handleConclusionChange,
    handleConclusionFocus,
    handleConclusionBlur,
    findings,
    setFindings,
    handleFindingChange,
    conclusionEditorRef,
    measurementsRecord,
    measurementToolByCanonical,
    annotatedMeasurements,
    unsavedMeasurements,
    contextRecord,
  };
}
